import React, { useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Globe,
  LayoutDashboard,
  Mail,
  MonitorSmartphone,
  Phone,
  Search,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Star,
  Zap
} from 'lucide-react';
import { push, ref } from 'firebase/database';
import SEO from './components/SEO';
import { db } from './firebase';
import { sendBriefEmail } from './email';
import logo from './logo.png';
import './App.css';

const SERVICE_CARDS = [
  {
    icon: Globe,
    title: 'Site vitrine',
    description: 'Une presence claire, rassurante et performante pour convertir vos visiteurs en prospects.',
    features: ['Design sur mesure', 'SEO technique', 'Responsive premium'],
    cta: 'Lancer un site vitrine'
  },
  {
    icon: ShoppingCart,
    title: 'E-commerce',
    description: 'Une boutique rapide et structuree pour vendre avec un parcours d achat fluide.',
    features: ['Paiement securise', 'Catalogue evolutif', 'Gestion des commandes'],
    cta: 'Construire une boutique'
  },
  {
    icon: LayoutDashboard,
    title: 'Application web',
    description: 'Un outil metier sur mesure pense pour la productivite, la croissance et l automatisation.',
    features: ['Developpement sur mesure', 'API et integrations', 'Dashboard clair'],
    cta: 'Concevoir une application'
  }
];

const TRUST_ITEMS = [
  { icon: Clock3, label: 'Devis gratuit sous 24h' },
  { icon: ShieldCheck, label: 'Livraison garantie' },
  { icon: Search, label: 'Optimise SEO' },
  { icon: MonitorSmartphone, label: 'Responsive' }
];

const HERO_BADGES = ['Devis sous 24h', 'Design premium', 'Accompagnement dedie'];

const TESTIMONIALS = [
  {
    name: 'Claire Dubois',
    role: 'Dirigeante',
    company: 'Cabinet Horizon',
    avatar: 'CD',
    quote:
      'Le nouveau site inspire confiance des les premieres secondes. Nous avons obtenu plus de demandes qualifiees en quelques semaines.'
  },
  {
    name: 'Mehdi Rahmani',
    role: 'Fondateur',
    company: 'Maison Atlas',
    avatar: 'MR',
    quote:
      'WebCaliskan a structure notre projet avec beaucoup de clarte. Le rendu est premium, rapide et simple a administrer.'
  },
  {
    name: 'Sophie Martin',
    role: 'Responsable marketing',
    company: 'Studio Nova',
    avatar: 'SM',
    quote:
      'Nous avions besoin d une vitrine serieuse et moderne. Le niveau de finition est excellent et l accompagnement tres rassurant.'
  }
];

const FAQ_ITEMS = [
  {
    question: 'Combien coute un site ?',
    answer:
      'Le budget depend du nombre de pages, des integrations et du niveau de personnalisation. Vous recevez un devis detaille sous 24h avec un cadre clair.'
  },
  {
    question: 'Combien de temps faut-il pour livrer ?',
    answer:
      'Un site vitrine peut etre livre rapidement. Un e-commerce ou une application web sont planifies par etapes avec un calendrier precis des le depart.'
  },
  {
    question: 'Comment se deroule le paiement ?',
    answer:
      'Le paiement se fait par jalons, valides ensemble avant chaque phase importante. Cela garantit de la visibilite et une collaboration sereine.'
  }
];

const BUDGET_OPTIONS = ['Moins de 1500 EUR', '1500 - 3000 EUR', '3000 - 5000 EUR', 'Plus de 5000 EUR'];
const DEADLINE_OPTIONS = ['Moins de 30 jours', '1 a 2 mois', '2 a 3 mois', 'Plus de 3 mois'];

const EMPTY_FORM = {
  nom: '',
  societe: '',
  email: '',
  telephone: '',
  typeProjet: '',
  budgetEstime: '',
  delaiSouhaite: '',
  descriptionProjet: ''
};

function isBudgetAbove3000(budget) {
  return /3000 - 5000|Plus de 5000/i.test(budget || '');
}

function isUrgent(deadline) {
  return /Moins de 30 jours/i.test(deadline || '');
}

function getProjectTypeScore(typeProjet) {
  if (/Application Web/i.test(typeProjet || '')) return 20;
  if (/E-commerce/i.test(typeProjet || '')) return 15;
  return 0;
}

function extractKeywords(description) {
  const normalized = (description || '').toLowerCase();
  const map = [
    { key: 'seo', regex: /seo|referencement/ },
    { key: 'e-commerce', regex: /e-?commerce|boutique|paiement|commande/ },
    { key: 'application', regex: /application|plateforme|dashboard|tableau de bord/ },
    { key: 'api', regex: /api|integration|crm|erp/ },
    { key: 'design', regex: /design|ui|ux|maquette/ },
    { key: 'performance', regex: /performance|rapide|optimisation/ }
  ];

  return map.filter((item) => item.regex.test(normalized)).map((item) => item.key);
}

function getComplexity(typeProjet, description) {
  const textLength = (description || '').length;
  if (/Application Web/i.test(typeProjet || '') || textLength > 400) return 'Elevee';
  if (/E-commerce/i.test(typeProjet || '') || textLength > 200) return 'Moyenne';
  return 'Faible';
}

function getPriority(score) {
  if (score >= 65) return 'Haute';
  if (score >= 35) return 'Moyenne';
  return 'Faible';
}

function buildSalesReply(formData, priority) {
  if (priority === 'Haute') {
    return `Bonjour ${formData.nom}, votre projet est prioritaire. Nous revenons vers vous aujourd hui avec un cadrage detaille.`;
  }
  if (priority === 'Moyenne') {
    return `Bonjour ${formData.nom}, merci pour votre demande. Nous vous envoyons un devis personnalise sous 24h.`;
  }
  return `Bonjour ${formData.nom}, merci pour votre message. Nous revenons vers vous avec une proposition claire et adaptee.`;
}

function buildRecommendations(formData, keywords, complexity, priority) {
  const recommendations = ['Proposer un appel de cadrage de 20 minutes.'];

  if (!formData.telephone) {
    recommendations.push('Demander un numero de telephone pour accelerer les echanges.');
  }
  if (!keywords.length) {
    recommendations.push('Clarifier les fonctionnalites prioritaires lors du premier appel.');
  }
  if (complexity === 'Elevee') {
    recommendations.push('Prevoir une phase de specification technique avant le chiffrage final.');
  }
  if (priority === 'Haute') {
    recommendations.push('Traiter le lead en priorite dans les 2 heures.');
  }

  return recommendations;
}

function buildLeadAnalysis(formData) {
  let score = 0;

  if (isBudgetAbove3000(formData.budgetEstime)) score += 30;
  if (isUrgent(formData.delaiSouhaite)) score += 20;
  score += getProjectTypeScore(formData.typeProjet);
  if (formData.telephone.trim()) score += 10;
  if ((formData.descriptionProjet || '').length > 200) score += 5;

  const keywords = extractKeywords(formData.descriptionProjet);
  const complexity = getComplexity(formData.typeProjet, formData.descriptionProjet);
  const priority = getPriority(score);
  const recommendations = buildRecommendations(formData, keywords, complexity, priority);

  return {
    score,
    priorite: priority,
    typeProjet: formData.typeProjet,
    budgetEstime: formData.budgetEstime,
    urgence: formData.delaiSouhaite,
    motsCles: keywords,
    complexite: complexity,
    recommandations: recommendations,
    reponseCommerciale: buildSalesReply(formData, priority)
  };
}

function ServiceCard({ card, onChoose }) {
  const Icon = card.icon;

  return (
    <motion.article
      className="service-card"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.35 }}
      whileHover={{ y: -5 }}
    >
      <div className="service-card__icon">
        <Icon size={22} aria-hidden="true" />
      </div>
      <h3>{card.title}</h3>
      <p className="service-card__description">{card.description}</p>
      <ul>
        {card.features.map((feature) => (
          <li key={feature}>
            <Check size={16} aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <button type="button" className="secondary-button" onClick={() => onChoose(card.title)}>
        {card.cta}
      </button>
    </motion.article>
  );
}

function TestimonialCard({ item }) {
  return (
    <motion.article
      className="testimonial-card"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.35 }}
    >
      <div className="testimonial-card__header">
        <div className="testimonial-avatar" aria-hidden="true">{item.avatar}</div>
        <div>
          <strong>{item.name}</strong>
          <span>{item.role} • {item.company}</span>
        </div>
      </div>
      <div className="testimonial-stars" aria-label="5 etoiles sur 5">
        {Array.from({ length: 5 }).map((_, index) => (
          <Star key={index} size={15} fill="currentColor" aria-hidden="true" />
        ))}
      </div>
      <p>“{item.quote}”</p>
    </motion.article>
  );
}

function FaqItem({ item, isOpen, onToggle, index }) {
  return (
    <article className={`faq-item${isOpen ? ' open' : ''}`}>
      <h3>
        <button
          type="button"
          className="faq-trigger"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={`faq-panel-${index}`}
        >
          <span>{item.question}</span>
          <ChevronDown size={18} aria-hidden="true" className={`faq-chevron${isOpen ? ' open' : ''}`} />
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`faq-panel-${index}`}
            className="faq-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <p>{item.answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </article>
  );
}

function App() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState({ success: false, message: '' });
  const [lastAnalysis, setLastAnalysis] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const formSectionRef = useRef(null);
  const servicesRef = useRef(null);

  const isFormValid = useMemo(() => {
    return (
      formData.nom.trim() &&
      formData.email.trim() &&
      formData.typeProjet.trim() &&
      formData.budgetEstime.trim() &&
      formData.delaiSouhaite.trim() &&
      formData.descriptionProjet.trim()
    );
  }, [formData]);

  const scrollToRef = (targetRef) => {
    targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleServiceChoice = (service) => {
    setFormData((prev) => ({ ...prev, typeProjet: service }));
    setErrors((prev) => ({ ...prev, typeProjet: '' }));
    scrollToRef(formSectionRef);
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.nom.trim()) nextErrors.nom = 'Le nom est requis.';
    if (!formData.email.trim()) nextErrors.email = 'L email est requis.';
    if (formData.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
      nextErrors.email = 'Format email invalide.';
    }
    if (!formData.typeProjet.trim()) nextErrors.typeProjet = 'Choisissez un type de projet.';
    if (!formData.budgetEstime.trim()) nextErrors.budgetEstime = 'Selectionnez un budget.';
    if (!formData.delaiSouhaite.trim()) nextErrors.delaiSouhaite = 'Selectionnez un delai.';
    if (!formData.descriptionProjet.trim()) nextErrors.descriptionProjet = 'La description est requise.';

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSubmitState({ success: false, message: 'Veuillez corriger les champs obligatoires.' });
      return;
    }

    setSubmitting(true);
    setSubmitState({ success: false, message: '' });

    const analysis = buildLeadAnalysis(formData);
    const payload = {
      ...formData,
      analyseLead: analysis,
      submittedAt: Date.now()
    };

    try {
      await push(ref(db, 'projectBriefs'), payload);
      await sendBriefEmail({
        nom_contact: formData.nom,
        entreprise: formData.societe,
        email: formData.email,
        telephone: formData.telephone,
        types_projet: [formData.typeProjet],
        description: formData.descriptionProjet,
        form_nom: formData.nom,
        form_societe: formData.societe,
        form_email: formData.email,
        form_telephone: formData.telephone,
        form_type_projet: formData.typeProjet,
        form_budget_estime: formData.budgetEstime,
        form_delai_souhaite: formData.delaiSouhaite,
        form_description_projet: formData.descriptionProjet,
        form_analyse_score: analysis.score,
        form_analyse_priorite: analysis.priorite
      });

      setLastAnalysis(analysis);
      setSubmitState({
        success: true,
        message: 'Votre demande a bien ete envoyee. Nous vous repondrons sous 24 heures.'
      });
      setFormData(EMPTY_FORM);
      scrollToRef(formSectionRef);
    } catch (error) {
      setSubmitState({
        success: false,
        message: 'Une erreur est survenue. Veuillez reessayer dans quelques minutes.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO />
      <div className="App">
        <main className="page-shell">
          <section className="hero-section premium-panel" aria-labelledby="hero-title">
            <div className="hero-grid">
              <motion.div
                className="hero-copy"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="hero-brand-row" aria-label="WebCaliskan">
                  <img className="hero-logo" src={logo} alt="Logo WebCaliskan" width="72" height="72" fetchPriority="high" />
                  <div className="hero-brand-copy">
                    <span className="hero-brand-name">WebCaliskan</span>
                    <span className="hero-brand-subtitle">Agence web premium</span>
                  </div>
                </div>

                <div className="hero-rating" aria-label="Note moyenne 4.9 sur 5 et plus de 50 projets realises">
                  <div className="hero-rating-stars" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star key={index} size={14} fill="currentColor" />
                    ))}
                  </div>
                  <span>4.9/5</span>
                  <span className="hero-rating-separator">•</span>
                  <span>Plus de 50 projets realises</span>
                </div>

                <h1 id="hero-title">Creation de sites web professionnels qui generent des clients.</h1>
                <p className="hero-subtitle">
                  Nous concevons des sites vitrines, e-commerce et applications web performants pour developper votre activite.
                </p>

                <div className="hero-badges" aria-label="Points forts WebCaliskan">
                  {HERO_BADGES.map((badge) => (
                    <span key={badge} className="hero-badge">
                      <Check size={14} aria-hidden="true" />
                      {badge}
                    </span>
                  ))}
                </div>

                <ul className="hero-trust-list" aria-label="Arguments de confiance">
                  {TRUST_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.label}>
                        <span className="hero-trust-icon"><Icon size={16} aria-hidden="true" /></span>
                        <span>{item.label}</span>
                      </li>
                    );
                  })}
                </ul>

                <div className="hero-actions">
                  <button type="button" className="primary-button" onClick={() => scrollToRef(formSectionRef)}>
                    <span>Obtenir mon devis</span>
                    <ArrowRight size={16} aria-hidden="true" />
                  </button>
                  <button type="button" className="ghost-button" onClick={() => scrollToRef(servicesRef)}>
                    Voir nos services
                  </button>
                </div>
              </motion.div>

              <motion.div
                className="hero-visual"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.05 }}
                aria-hidden="true"
              >
                <div className="hero-glow hero-glow--one" />
                <div className="hero-glow hero-glow--two" />
                <div className="mockup-window">
                  <div className="mockup-topbar">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="mockup-shell">
                    <aside className="mockup-sidebar">
                      <div className="mockup-chip"><Sparkles size={14} /> Branding</div>
                      <div className="mockup-chip"><Search size={14} /> SEO</div>
                      <div className="mockup-chip"><ShieldCheck size={14} /> Conversion</div>
                    </aside>
                    <div className="mockup-screen">
                      <div className="mockup-hero-card">
                        <span className="mockup-pill">Interface premium</span>
                        <strong>Structure claire, rapide et rassurante</strong>
                        <p>Une experience moderne qui met en valeur votre activite et favorise la prise de contact.</p>
                      </div>
                      <div className="mockup-stats-grid">
                        <div>
                          <span>Conversion</span>
                          <strong>+32%</strong>
                        </div>
                        <div>
                          <span>Performance</span>
                          <strong>98/100</strong>
                        </div>
                        <div>
                          <span>SEO</span>
                          <strong>Technique propre</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <ul className="hero-bottom-bar">
              <li><Clock3 size={16} aria-hidden="true" /> Reponse en 24h</li>
              <li><Zap size={16} aria-hidden="true" /> Processus simple</li>
              <li><MonitorSmartphone size={16} aria-hidden="true" /> Experience mobile soignee</li>
            </ul>
          </section>

          <section className="lead-section" id="formulaire" aria-labelledby="lead-title">
            <motion.div
              className="lead-copy premium-panel"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.35 }}
            >
              <span className="section-kicker">Devis gratuit</span>
              <h2 id="lead-title">Un cadrage clair, un budget coherent, une execution serieuse.</h2>
              <p>
                Nous structurons votre besoin, priorisons l essentiel et revenons avec une proposition propre, comprehensible et actionnable.
              </p>
              <ul className="lead-points">
                <li><CheckCircle2 size={16} aria-hidden="true" /> Analyse du besoin</li>
                <li><CheckCircle2 size={16} aria-hidden="true" /> Budget et delais clairs</li>
                <li><CheckCircle2 size={16} aria-hidden="true" /> Recommandations concretes</li>
              </ul>

              {lastAnalysis && (
                <article className="admin-analysis" aria-label="Analyse administrateur">
                  <div className="admin-analysis__title">
                    <Sparkles size={16} aria-hidden="true" />
                    <h3>Analyse automatique du lead</h3>
                  </div>
                  <ul>
                    <li><span>Score</span><strong>{lastAnalysis.score}</strong></li>
                    <li><span>Priorite</span><strong>{lastAnalysis.priorite}</strong></li>
                    <li><span>Projet</span><strong>{lastAnalysis.typeProjet}</strong></li>
                    <li><span>Budget</span><strong>{lastAnalysis.budgetEstime}</strong></li>
                    <li><span>Urgence</span><strong>{lastAnalysis.urgence}</strong></li>
                    <li><span>Mots cles</span><strong>{lastAnalysis.motsCles.join(', ') || 'Aucun'}</strong></li>
                    <li><span>Complexite</span><strong>{lastAnalysis.complexite}</strong></li>
                    <li><span>Recommandations</span><strong>{lastAnalysis.recommandations.join(' | ')}</strong></li>
                    <li><span>Reponse commerciale</span><strong>{lastAnalysis.reponseCommerciale}</strong></li>
                  </ul>
                </article>
              )}
            </motion.div>

            <motion.section
              className="lead-form-card premium-panel"
              ref={formSectionRef}
              aria-labelledby="form-title"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.35, delay: 0.05 }}
            >
              <div className="lead-form-card__header">
                <span className="form-badge">Recevoir mon devis</span>
                <h3 id="form-title">Recevoir mon devis gratuit</h3>
                <p>Nous vous repondons sous 24 heures avec une proposition claire.</p>
              </div>

              <form className="lead-form" onSubmit={handleSubmit} noValidate>
                <div className="lead-form__grid">
                  <label>
                    <span>Nom</span>
                    <input
                      type="text"
                      name="nom"
                      autoComplete="name"
                      placeholder="Votre nom"
                      value={formData.nom}
                      onChange={(event) => updateField('nom', event.target.value)}
                      aria-invalid={Boolean(errors.nom)}
                      aria-label="Nom"
                    />
                    {errors.nom && <span className="error-text">{errors.nom}</span>}
                  </label>

                  <label>
                    <span>Entreprise</span>
                    <input
                      type="text"
                      name="societe"
                      autoComplete="organization"
                      placeholder="Nom de votre entreprise"
                      value={formData.societe}
                      onChange={(event) => updateField('societe', event.target.value)}
                      aria-label="Entreprise"
                    />
                  </label>

                  <label>
                    <span>Email</span>
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder="vous@entreprise.com"
                      value={formData.email}
                      onChange={(event) => updateField('email', event.target.value)}
                      aria-invalid={Boolean(errors.email)}
                      aria-label="Email"
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </label>

                  <label>
                    <span>Telephone</span>
                    <input
                      type="tel"
                      name="telephone"
                      autoComplete="tel"
                      placeholder="06 00 00 00 00"
                      value={formData.telephone}
                      onChange={(event) => updateField('telephone', event.target.value)}
                      aria-label="Telephone"
                    />
                  </label>

                  <label>
                    <span>Type de projet</span>
                    <select
                      name="typeProjet"
                      value={formData.typeProjet}
                      onChange={(event) => updateField('typeProjet', event.target.value)}
                      aria-invalid={Boolean(errors.typeProjet)}
                      aria-label="Type de projet"
                    >
                      <option value="">Selectionner</option>
                      <option value="Site vitrine">Site vitrine</option>
                      <option value="E-commerce">E-commerce</option>
                      <option value="Application Web">Application web</option>
                    </select>
                    {errors.typeProjet && <span className="error-text">{errors.typeProjet}</span>}
                  </label>

                  <label>
                    <span>Budget</span>
                    <select
                      name="budgetEstime"
                      value={formData.budgetEstime}
                      onChange={(event) => updateField('budgetEstime', event.target.value)}
                      aria-invalid={Boolean(errors.budgetEstime)}
                      aria-label="Budget"
                    >
                      <option value="">Selectionner</option>
                      {BUDGET_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {errors.budgetEstime && <span className="error-text">{errors.budgetEstime}</span>}
                  </label>

                  <label>
                    <span>Delais</span>
                    <select
                      name="delaiSouhaite"
                      value={formData.delaiSouhaite}
                      onChange={(event) => updateField('delaiSouhaite', event.target.value)}
                      aria-invalid={Boolean(errors.delaiSouhaite)}
                      aria-label="Delais"
                    >
                      <option value="">Selectionner</option>
                      {DEADLINE_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {errors.delaiSouhaite && <span className="error-text">{errors.delaiSouhaite}</span>}
                  </label>
                </div>

                <label className="lead-form__full">
                  <span>Description</span>
                  <textarea
                    rows="5"
                    name="descriptionProjet"
                    autoComplete="off"
                    placeholder="Decrivez votre besoin, vos objectifs et le contexte du projet."
                    value={formData.descriptionProjet}
                    onChange={(event) => updateField('descriptionProjet', event.target.value)}
                    aria-invalid={Boolean(errors.descriptionProjet)}
                    aria-label="Description du projet"
                  />
                  <small>Decrivez simplement votre besoin. Nous nous occupons du reste.</small>
                  {errors.descriptionProjet && <span className="error-text">{errors.descriptionProjet}</span>}
                </label>

                <button type="submit" className="primary-button form-submit-button" disabled={submitting || !isFormValid}>
                  {submitting ? (
                    <>
                      <span className="button-spinner" aria-hidden="true" />
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <>
                      <span>Recevoir mon devis gratuit</span>
                      <ArrowRight size={16} aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>

              {submitState.message && (
                <div className={`submit-feedback ${submitState.success ? 'success' : 'error'}`} role="status" aria-live="polite">
                  {submitState.success ? <CheckCircle2 size={20} aria-hidden="true" /> : <Sparkles size={20} aria-hidden="true" />}
                  <div>
                    {submitState.success && <strong>Merci !</strong>}
                    <p>{submitState.message}</p>
                  </div>
                </div>
              )}
            </motion.section>
          </section>

          <section className="services-section" ref={servicesRef} aria-labelledby="services-title">
            <div className="section-heading">
              <span className="section-kicker">Services</span>
              <h2 id="services-title">Des solutions premium concues pour convertir et durer.</h2>
              <p>Chaque offre est structuree pour allier design, clarte, performance et impact commercial.</p>
            </div>
            <div className="services-grid premium-grid">
              {SERVICE_CARDS.map((card) => (
                <ServiceCard key={card.title} card={card} onChoose={handleServiceChoice} />
              ))}
            </div>
          </section>

          <section className="testimonials-section" aria-labelledby="testimonials-title">
            <div className="section-heading">
              <span className="section-kicker">Temoignages</span>
              <h2 id="testimonials-title">Une execution qui inspire confiance des les premiers echanges.</h2>
              <p>Des projets suivis avec exigence, clarte et un vrai niveau de finition.</p>
            </div>
            <div className="testimonials-grid premium-grid">
              {TESTIMONIALS.map((item) => (
                <TestimonialCard key={item.name} item={item} />
              ))}
            </div>
          </section>

          <section className="faq-section" aria-labelledby="faq-title">
            <div className="section-heading section-heading--centered">
              <span className="section-kicker">FAQ</span>
              <h2 id="faq-title">Les reponses essentielles avant de lancer votre projet.</h2>
            </div>
            <div className="faq-list premium-panel">
              {FAQ_ITEMS.map((item, index) => (
                <FaqItem
                  key={item.question}
                  item={item}
                  index={index}
                  isOpen={openFaqIndex === index}
                  onToggle={() => setOpenFaqIndex(openFaqIndex === index ? -1 : index)}
                />
              ))}
            </div>
          </section>
        </main>

        <footer className="site-footer">
          <div className="footer-shell premium-panel">
            <div className="footer-brand">
              <div>
                <strong>WebCaliskan</strong>
                <span>Sites web, e-commerce et applications sur mesure.</span>
              </div>
            </div>
            <nav className="footer-links" aria-label="Liens de contact et mentions legales">
              <a href="tel:+33649876236" aria-label="Appeler WebCaliskan">
                <Phone size={16} aria-hidden="true" />
                <span>+33 6 49 87 62 36</span>
              </a>
              <a href="mailto:webcaliskan@gmail.com" aria-label="Envoyer un email a WebCaliskan">
                <Mail size={16} aria-hidden="true" />
                <span>webcaliskan@gmail.com</span>
              </a>
              <a href="mailto:webcaliskan@gmail.com?subject=Demande%20de%20mentions%20legales" aria-label="Demander les mentions legales par email">
                <ShieldCheck size={16} aria-hidden="true" />
                <span>Demander les mentions legales</span>
              </a>
            </nav>
            <div className="footer-copy">© 2026 WebCaliskan</div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default App;
